import {
	Component,
	Inject,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { Router } from "@angular/router";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";

import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { cloneDeep } from "lodash";

import { BehaviorSubject, EMPTY, Observable, Subscription } from "rxjs";
import { catchError, finalize, map, shareReplay, tap } from "rxjs/operators";

import { TranslateService } from "@ngx-translate/core";
import { PaymentModesService } from "src/app/shared/services/payment-modes/payment-modes.service";
import { PosMovementsService } from "src/app/shared/services/pos-movements/posMovements.service";
import { SnackbarService } from "src/app/shared/services/snackbar/snackbar.service";

import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { DraftDocumentOutDTO } from "src/app/models/movements/movements";
import { AutocompleteSelectItem } from "src/app/models/selection-list/autocomplete-select-item.model";
import { PaymentModeDTO } from "src/app/models/payment-modes/paymentModeDTO";

import { FormUtils } from "src/app/shared/utils/formUtils";
import { toFixedNoRounding } from "src/app/shared/utils/decimalPlacesUtils";

export const FORM_PARAMS = {
  paymentModes: "paymentModes",
	paymentModeCode: "paymentModeCode",
	paymentModeDescription: "paymentModeDescription",
  paymentModeAmount: "paymentModeAmount",
  paymentModeMovementCode: "paymentModeMovementCode",
};

@Component({
	selector: "app-change-mop-dialog",
	templateUrl: "./change-mop-dialog.component.html",
	styleUrls: ["./change-mop-dialog.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class ChangeMopComponent implements OnInit, OnDestroy {
	//Movement
	isLoading$ = new BehaviorSubject<boolean>(false);
	currentDocument: any;

	//Form
	mopDataForm: FormGroup;

  //Payment Mode Search
  isLoadingPaymentModes$ = new BehaviorSubject<boolean>(false);
  paymentModeList$: Observable<PaymentModeDTO[]>;
  paymentModeList: AutocompleteSelectItem[] = [];
  searchTimer: number;

	//Subscriptions
	loadDocumentInfoSubscription: Subscription;
	updateDocumentBuilderSaveSubscription: Subscription;

	constructor(
		@Inject(MAT_DIALOG_DATA) private data: any,
		private dialogRef: MatDialogRef<ChangeMopComponent>,
		private fb: FormBuilder,
		private posMovementsService: PosMovementsService,
    private paymentModesService: PaymentModesService,
		private snackbarService: SnackbarService,
		private translate: TranslateService,
		private router: Router,
	) {	}

	ngOnInit(): void {
		this.creatMopForm();
		this.loadDocumentInfo();
    this.setDocumentFormPaymentModes(this.data.treatedPaymentsArr);
	}

	creatMopForm() {
		this.mopDataForm = this.fb.group({
      [FORM_PARAMS.paymentModes]: this.fb.array([]),
    });
	}

  loadDocumentInfo() {
		this.isLoading$.next(true);
		this.loadDocumentInfoSubscription = this.posMovementsService
			.getDocumentBuilderOut(this.data.documentToken)
			.pipe(
				catchError((error) => {
					this.snackbarService.show({
						message: this.translate.instant("PosMovements.documentLoadError"),
						type: SnackbarType.ERROR,
					});
					return EMPTY;
				}),
				finalize(() => this.isLoading$.next(false))
			)
			.subscribe((document) => {
				this.currentDocument = cloneDeep(document);
			});
	}

	setDocumentFormPaymentModes(treatedPaymentsArr: any[]) {
    const documentPaymentMode = this.getFormArray(FORM_PARAMS.paymentModes);
    this.clearFormArray(documentPaymentMode);

    treatedPaymentsArr.forEach((pm) => {
      documentPaymentMode.push(this.createDocumentPaymentModesFormGroup(pm));
    });
  }

  createDocumentPaymentModesFormGroup(paymentMode?: any, value = 0): FormGroup {
    return this.fb.group({
      [FORM_PARAMS.paymentModeCode]: [
        { 
          value: paymentMode?.replaceable ? null : { code: paymentMode?.paymentModeCode, description: paymentMode?.description }, 
          disabled: paymentMode && !paymentMode.replaceable ? true : false
        },
        [FormUtils.isObjectValidator, Validators.required]
      ],
      [FORM_PARAMS.paymentModeDescription]: [ paymentMode?.description ],
      [FORM_PARAMS.paymentModeAmount]: [
        {
          value: paymentMode?.amount 
          ? toFixedNoRounding(paymentMode?.amount, this.data.amountDecimalPlaces) 
          : toFixedNoRounding(value, this.data.amountDecimalPlaces),
          disabled: true,
        },
        [Validators.required, Validators.min(0), Validators.max(999999999)]
      ],
      [FORM_PARAMS.paymentModeMovementCode]: [
        paymentMode?.movementCode ?? this.data.documentTypes.find(el => el.token === this.data.documentTypeToken).documentNatureCode,
      ],
    });
  }

  getPaymentModeDescriptionPlaceholder(paymentModeControl: FormGroup): string {
    const value = paymentModeControl.get('paymentModeDescription')?.value;
    return value ? value : '';
  }

  getFormArray(formParam: string): FormArray {
    return this.mopDataForm.get(formParam) as FormArray;
  }

  clearFormArray(formArray: FormArray) {
    formArray.clear();
  }

  validatePaymentModeSearch() {
      this.searchPaymentModes('');
  }

  searchPaymentModes(event: KeyboardEvent | string, hasTimer = true): void {
    const searchTerm = typeof event === 'string' ? event : (event.target as HTMLInputElement).value;
    clearTimeout(this.searchTimer);
    this.searchTimer = window.setTimeout(
      () => {
        this.isLoadingPaymentModes$.next(true);
        this.paymentModeList$ = this.paymentModesService
          .getPaymentModesList(null, {
            filterConfig: [{ field: "code" }, { field: "description" }],
            searchTerms: [searchTerm],
          })
          .pipe(
            map(({ items }) =>  {
              const paymentModesAllowReplace = items.filter((item) => item.exchangeable);

              const selectedDocTypeToken = this.data.documentTypeToken;
              const currDocTypeObj = this.data.documentTypes.find((documentType) => documentType.token === selectedDocTypeToken);

              const addedPaymentModeCodes = (this.mopDataForm.controls[FORM_PARAMS.paymentModes] as FormGroup)
                .getRawValue()
                .map(paymentMode => paymentMode.paymentModeCode)
                .map((addedPaymentMode) => addedPaymentMode?.code);

              if (currDocTypeObj.paymentModeCode) {
                return paymentModesAllowReplace.filter((item) => item.code === currDocTypeObj.paymentModeCode && !addedPaymentModeCodes.includes(item.code));
              }

              return paymentModesAllowReplace.filter((item) => {
                if (addedPaymentModeCodes && addedPaymentModeCodes.includes(undefined)) {
                  return !addedPaymentModeCodes.includes(item.code);
                } else {
                  return !addedPaymentModeCodes.includes(item);
                }
              });
            }),
            catchError((error) => {
              this.snackbarService.show({
                message: this.translate.instant("PosMovements.errorLoadingPaymentModes"),
                type: SnackbarType.ERROR,
              });
              return EMPTY;
            }),
            finalize(() => this.isLoadingPaymentModes$.next(false)),
            shareReplay()
          );
      },
      hasTimer ? 500 : 0
    );
  }

  autoPaymentModeCode(event: MatAutocompleteSelectedEvent, paymentModesControl: FormGroup) {
    const {
      option: { value: selectedPaymentModeCode },
    } = event;
    paymentModesControl.get(FORM_PARAMS.paymentModeCode).setValue(selectedPaymentModeCode.paymentModeCode);
    paymentModesControl.get(FORM_PARAMS.paymentModeAmount).setValue(selectedPaymentModeCode.paymentModeAmount);
  }

  displayPaymentModeCode(paymentMode: { code: string; description: string }): string {
    return paymentMode?.description ?? "";
  }

  addDocumentPaymentMode() {
    const docPaymentModes = this.getFormArray(FORM_PARAMS.paymentModes);
    const value = docPaymentModes.value.length ? 0 : this.data.totalGrossAmount;
    docPaymentModes.push(this.createDocumentPaymentModesFormGroup(null, value));
  }

  removeDocumentPaymentMode(index: number) {
    const docPaymentModes = this.getFormArray(FORM_PARAMS.paymentModes);
    docPaymentModes.removeAt(index);
    this.mopDataForm.markAsDirty();

  }

	saveDocument() {
		const draftDocumentOut = this.setDraftDocumentOut();
		if (!this.validateForm(this.mopDataForm)) {
			return;
		}
		this.updateDocumentBuilderSave(draftDocumentOut);
	}

  
	validateForm(form: FormGroup) {
		if (!form.valid) {
			form.markAllAsTouched();
			this.snackbarService.show({
				message: this.translate.instant("PosMovements.errorValidatingForm"),
				type: SnackbarType.ERROR,
			});
			return false;
		}
		return true;
	}

  isFormInvalid() {
    return this.mopDataForm.invalid || !this.mopDataForm.dirty;
  }

  setDraftDocumentOut() {
		return {
			...this.currentDocument,
			actionType: "CHANGE_PAYMENT_MODE",
			paymentModeCodes: this.setPaymentModes(this.mopDataForm),
		};
	}

  setPaymentModes(form: FormGroup) {
    const paymentModes = (
      form.controls[FORM_PARAMS.paymentModes] as FormGroup
    ).getRawValue();
    const paymentModesTreated = paymentModes.map((paymentMode) => {
      return {
        paymentModeCode: paymentMode.paymentModeCode?.code ? paymentMode.paymentModeCode?.code : null,
        amount: paymentMode.paymentModeAmount ? Number(paymentMode.paymentModeAmount) : 0,
        movementCode: paymentMode.paymentModeMovementCode ?? null,
      };
    });
    return paymentModesTreated;
  }

  updateDocumentBuilderSave(draftDocumentOut: DraftDocumentOutDTO) {
		this.isLoading$.next(true);
		this.updateDocumentBuilderSaveSubscription = this.posMovementsService
			.updateDocumentBuilderOutSave(draftDocumentOut)
			.pipe(
				catchError((error) => {
					this.snackbarService.show({
						message:
							this.translate.instant(error) ??
							this.translate.instant("PosMovements.documentSaveFail"),
						type: SnackbarType.ERROR,
					});
					return EMPTY;
				}),
				finalize(() => this.isLoading$.next(false))
			)
			.subscribe((updatedDocument) => {
				if (updatedDocument) {
					updatedDocument.listOfDocumentsToPrint.forEach((token) =>
						this.printDocument(token)
					);
					this.dialogRef.close();
					this.router.navigate(["/movements-customer-headers"]);
				}
			});
	}

  printDocument(token: string) {
		this.isLoading$.next(true);
		this.posMovementsService
			.printDocument(token)
			.pipe(
				tap((response: any) => {
					const pdfSrc = response[0].data;
					const byteArray = new Uint8Array(
						atob(pdfSrc)
							.split("")
							.map((char) => char.charCodeAt(0))
					);
					const blob = new Blob([byteArray], { type: "application/pdf" });
					const url = window.URL.createObjectURL(blob);
					window.open(url, "_blank");
				}),
				catchError((error) => {
					this.snackbarService.show({
						message:
							this.translate.instant(error) ??
							this.translate.instant("Reports.GeneratingReportError"),
						type: SnackbarType.ERROR,
					});
					return EMPTY;
				}),
				finalize(() => this.isLoading$.next(false))
			)
			.subscribe();
	}

	ngOnDestroy() {
		if (this.loadDocumentInfoSubscription) {
			this.loadDocumentInfoSubscription.unsubscribe();
		}
		if (this.updateDocumentBuilderSaveSubscription) {
			this.updateDocumentBuilderSaveSubscription.unsubscribe();
		}
	}
}
